import { ReactNode, useEffect, useState } from "react";

import { ProfileResource } from "@medplum/core";
import { useMedplum, useMedplumProfile } from "@medplum/react";

import { Alert, Box, Button, Checkbox, Group, Paper, Stack, UnstyledButton } from "@mantine/core";

import { IconAlertCircle, IconCheck } from "@tabler/icons-react";

import { Loading } from "@/components/Loading";
import { TextWrapper } from "@/components/TextWrapper";
import { toast } from "@/utils/notifications";

import {
  fetchNotificationPreferencesProfile,
  saveNotificationPreferences,
} from "../services/notification-preferences";
import { NOTIFICATION_PREFERENCES } from "../types/NotificationsPreference";
import { getSelectedNotificationPreferenceIds } from "../utils";

type ProfileLoadState =
  | { status: "loading" }
  | { status: "loaded"; profile: ProfileResource }
  | { status: "failed" };

interface MyPreferencesProps {
  onCancel: () => void;
  onSaved: () => void;
}

export default function MyPreferences({ onCancel, onSaved }: MyPreferencesProps) {
  const signedInProfile = useMedplumProfile();
  const medplum = useMedplum();
  const [profileLoadState, setProfileLoadState] = useState<ProfileLoadState>({
    status: "loading",
  });
  const [selectedPreferences, setSelectedPreferences] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!signedInProfile) return;

    let ignoreFetch = false;

    fetchNotificationPreferencesProfile(medplum, signedInProfile)
      .then((latestProfile) => {
        if (ignoreFetch) return;
        setSelectedPreferences(getSelectedNotificationPreferenceIds(latestProfile));
        setProfileLoadState({ status: "loaded", profile: latestProfile });
      })
      .catch(() => {
        if (!ignoreFetch) setProfileLoadState({ status: "failed" });
      });

    return () => {
      ignoreFetch = true;
    };
  }, [medplum, signedInProfile]);

  const handlePreferenceChange = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedPreferences((prev) => [...prev, id]);
    } else {
      setSelectedPreferences((prev) => prev.filter((prefId) => prefId !== id));
    }
  };

  const handleSave = async (profile: ProfileResource) => {
    setIsSaving(true);
    const result = await saveNotificationPreferences(medplum, profile, selectedPreferences);
    setIsSaving(false);

    if (!result.ok && result.reason === "stale_profile") {
      toast({
        title: "Preferences not saved",
        message:
          "Your profile changed since you opened Notification Settings. Close and reopen it, then save again.",
        type: "error",
        autoClose: false,
      });
      return;
    }

    if (!result.ok) {
      toast({
        title: "Something went wrong",
        message: "Error saving preferences",
        type: "error",
        autoClose: 3000,
      });
      return;
    }

    toast({
      title: "Preferences saved",
      message: "Preferences saved successfully",
      type: "success",
      autoClose: 3000,
    });
    onSaved();
  };

  let content: ReactNode = <Loading height={200} />;
  if (profileLoadState.status === "failed") {
    content = (
      <Alert
        color="red"
        icon={<IconAlertCircle size={16} />}
        title="Unable to load your preferences"
      >
        Close this panel and open Notification Settings again.
      </Alert>
    );
  } else if (profileLoadState.status === "loaded") {
    content = (
      <NotificationPreferenceList
        selectedPreferences={selectedPreferences}
        onPreferenceChange={handlePreferenceChange}
      />
    );
  }

  return (
    <>
      {content}

      <Group mt="xl" justify="flex-end">
        <Button
          px="xl"
          size="md"
          variant="outline"
          onClick={onCancel}
          disabled={isSaving}
          data-testid="notification-preferences-cancel"
        >
          Cancel
        </Button>
        {profileLoadState.status === "loaded" && (
          <Button
            px="xl"
            size="md"
            color="burgundy.9"
            loading={isSaving}
            data-testid="notification-preferences-save"
            onClick={() => handleSave(profileLoadState.profile)}
          >
            Save
          </Button>
        )}
      </Group>
    </>
  );
}

interface NotificationPreferenceListProps {
  selectedPreferences: string[];
  onPreferenceChange: (id: string, checked: boolean) => void;
}

function NotificationPreferenceList({
  selectedPreferences,
  onPreferenceChange,
}: NotificationPreferenceListProps) {
  return (
    <Paper radius="md" color="white">
      <Group px="xl" py="lg" bg="gray.1" justify="space-between">
        <TextWrapper c="gray.6" variant="bold">
          Type
        </TextWrapper>
        <TextWrapper c="gray.6" variant="bold">
          Opt In
        </TextWrapper>
      </Group>

      <Stack gap={0}>
        {NOTIFICATION_PREFERENCES.map((preference, index) => {
          const isSelected = selectedPreferences.includes(preference.id);

          return (
            <Box key={preference.id}>
              <UnstyledButton
                w="100%"
                data-testid={`notification-preference-${preference.id}`}
                onClick={() => onPreferenceChange(preference.id, !isSelected)}
                style={{
                  borderBottom:
                    index < NOTIFICATION_PREFERENCES.length - 1
                      ? "1px solid var(--mantine-color-gray-3)"
                      : "none",
                }}
              >
                <Group
                  px="xl"
                  py="lg"
                  justify="space-between"
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = "transparent";
                  }}
                  style={{
                    cursor: "pointer",
                    transition: "background-color 0.15s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = "var(--mantine-color-gray-0)";
                  }}
                >
                  <TextWrapper c="dark.8" variant="bold">
                    {preference.label}
                  </TextWrapper>
                  <Checkbox
                    readOnly
                    size="md"
                    color="burgundy.9"
                    checked={isSelected}
                    aria-label={preference.label}
                    icon={({ className }) => <IconCheck size={16} className={className} />}
                    styles={{
                      input: {
                        backgroundColor: isSelected
                          ? "var(--mantine-color-burgundy-9)"
                          : "transparent",
                        borderColor: "var(--mantine-color-burgundy-9)",
                        borderWidth: "2px",
                        "&:checked": {
                          backgroundColor: "var(--mantine-color-burgundy-9)",
                          borderColor: "var(--mantine-color-burgundy-9)",
                        },
                      },
                    }}
                  />
                </Group>
              </UnstyledButton>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}
